import { Clickable, ClickHandler } from 'dg-web-shared/ui/Clickable.tsx';
import { Icon16, Icon24 } from '../icons/Icon.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { Maybe, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { css } from '@emotion/css';
import { clearfix } from 'dg-web-shared/ui/clearfix.ts';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { ColorHex } from '../Colors.ts';
import React, { type JSX } from 'react';

export enum SwitchType {
    Node,
    RadioButton,
    Checkbox,
}

interface SwitchIconProps {
    className: string;
    selected: boolean;
    allSelected?: boolean;
    type: SwitchType;
}

const SwitchIcon = (p: SwitchIconProps): JSX.Element => (
    <div className={p.className}>
        <Icon16
            icon={
                p.type === SwitchType.Node
                    ? p.selected
                        ? p.allSelected
                            ? Icons16.node.all
                            : Icons16.node.on
                        : Icons16.node.off
                    : p.type === SwitchType.Checkbox
                      ? p.selected
                          ? p.allSelected
                              ? Icons16.checkbox.all
                              : Icons16.checkbox.on
                          : Icons16.checkbox.off
                      : p.selected
                        ? Icons16.radiobutton.on
                        : Icons16.radiobutton.off
            }
        />
    </div>
);

interface SwitchProps {
    label: React.ReactNode;
    selected: boolean;
    onClick: ClickHandler;
    type: SwitchType;
    icon?: Maybe<JSX.Element>;
    disabled?: boolean;
}

export function Switch({
    label,
    selected,
    onClick,
    type,
    icon,
    disabled,
}: SwitchProps) {
    return (
        <Clickable
            className={css([
                {
                    height: '24px',
                    color: selected
                        ? ColorHex.darkblue
                        : ColorHex.rgba(ColorHex.darkblue, 0.6),
                    ...Typo.captionC1,
                    display: 'inline-block',
                    '&:hover': {
                        color:
                            disabled || selected
                                ? ColorHex.rgba(ColorHex.darkblue, 0.6)
                                : ColorHex.darkblue,
                    },
                },
                clearfix,
            ])}
            element="div"
            onClick={!disabled ? onClick : null}
            data-selected={selected}
            data-type={type === SwitchType.Node ? 'checkbox' : 'radiobutton'}
            disabled={disabled}
        >
            <SwitchIcon
                className={css({
                    paddingTop: '4px',
                    float: 'left',
                })}
                type={type}
                selected={selected}
            />
            {thenElse(
                icon,
                i => (
                    <Icon24 icon={i} />
                ),
                null,
            )}
            <div
                className={css({
                    marginLeft: '8px',
                    paddingTop: '5px',
                    float: 'left',
                })}
            >
                {label}
            </div>
        </Clickable>
    );
}

export interface RadioButtonProps {
    label: string;
    selected: boolean;
    onClick: ClickHandler;
    icon?: Maybe<JSX.Element>;
    disabled?: boolean;
}

export const RadioButton = (p: RadioButtonProps): JSX.Element => (
    <Switch {...p} type={SwitchType.RadioButton} />
);
